
import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { UserCard } from "./UserCard";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LoadingFallback } from "@/components/common/LoadingFallback";
import { UserSearch } from "lucide-react";

interface FollowersListProps {
  profileId: string;
  type: "followers" | "following";
}

export const FollowersList: React.FC<FollowersListProps> = ({ profileId, type }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let query;
        
        if (type === "followers") {
          // People following this profile
          const { data, error } = await supabase
            .from("followers")
            .select(`
              follower_id,
              profiles!followers_follower_id_fkey (
                id, 
                username, 
                display_name, 
                profile_image_url, 
                banner_image_url,
                registration_number,
                elo_rating,
                is_admin,
                is_premium
              )
            `)
            .eq("following_id", profileId);
            
          if (error) throw error;
          
          // Transform the data to get the profiles
          const transformedData = data.map(item => item.profiles);
          setUsers(transformedData);
        } else {
          // People this profile is following
          const { data, error } = await supabase
            .from("followers")
            .select(`
              following_id,
              profiles!followers_following_id_fkey (
                id, 
                username, 
                display_name, 
                profile_image_url, 
                banner_image_url,
                registration_number,
                elo_rating,
                is_admin,
                is_premium
              )
            `)
            .eq("follower_id", profileId);
            
          if (error) throw error;
          
          // Transform the data to get the profiles
          const transformedData = data.map(item => item.profiles);
          setUsers(transformedData);
        }
      } catch (err) {
        console.error(`Error fetching ${type}:`, err);
        setError(`Failed to load ${type}. Please try again.`);
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      fetchUsers();
    }
  }, [profileId, type]);

  if (loading) {
    return <LoadingFallback />;
  }

  if (error) {
    return <div className="text-red-400 p-4 text-center">{error}</div>;
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <UserSearch size={24} className="text-gray-400 mb-2" />
        <p className="text-gray-400">
          {type === "followers" 
            ? "No followers yet" 
            : "Not following anyone yet"}
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[400px] pr-2">
      <div className="space-y-3">
        {users.map((user) => (
          <UserCard key={user.id} user={user} />
        ))}
      </div>
    </ScrollArea>
  );
};
